import React, { useCallback, useMemo, useState } from "react";
import axios from "axios";

import { Select } from "../ui";
import EditUserButton from "../EditUserButton";
import "./select.scss";

const defaultOptionsUrl = "/api/users/search";

/**
 * The option label every consumer of this picker shows: person name, plus the
 * store when the record has one. Exported so a caller that re-reads a user
 * (after it was edited in another tab) can rebuild the same label.
 */
export const userOptionName = (user) => {
  if (!user) return "";
  if (user.name && user.first_name == null) return user.name;
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
  return user.store?.name ? `${name} | ${user.store.name}` : name;
};

const mergeById = (prev, next) => {
  const map = new Map(prev.map((o) => [o.id, o]));
  for (const o of next) if (o?.id != null) map.set(o.id, o);
  return [...map.values()];
};

/**
 * Async user picker on components/ui/Select. Contract unchanged:
 *   value     — a scalar id (Formik path) or a full {id, name} option (Commissions)
 *   onChange  — (option, option); without it, Formik gets `field.name = option.id`
 *   defaultOptions — [option] seeds the label of a stored id (`true` = just search)
 *   editable  — edit-user icon on every result and beside the selected user
 * `defaultInputValue` (react-select's pre-filled search text) and `styles` have no
 * ui/Select equivalent and are ignored.
 */
const UserSearchSelect = ({
  className,
  field,
  form,
  id,
  placeholder,
  optionsUrl = defaultOptionsUrl,
  value,
  onChange,
  defaultOptions,
  disabled = false,
  editable = false,
}) => {
  // Every user this picker has seen, so a picked or stored id keeps its label.
  const [seen, setSeen] = useState([]);

  const stored = value ?? field?.value;
  const storedId = typeof stored === "object" && stored !== null ? stored.id : stored;
  const known = useMemo(
    () =>
      mergeById(seen, [
        ...(Array.isArray(defaultOptions) ? defaultOptions : []),
        ...(typeof stored === "object" && stored?.id != null ? [stored] : []),
      ]),
    [seen, defaultOptions, stored]
  );

  const toUi = useCallback(
    (user) => ({
      value: user.id,
      label: user.name,
      // The icon swallows its own mousedown/click, so it never picks the row.
      hint: editable ? <EditUserButton user={user} className="user-select__edit" /> : undefined,
      raw: user,
    }),
    [editable]
  );

  const loadOptions = useCallback(
    async (query) => {
      const response = await axios
        .post(optionsUrl, {
          filters: {},
          sort: { field: "created_at", direction: "desc" },
          query,
          paging: { offset: 0, limit: 15 },
        })
        // A failed search resolves to an empty list, never a stuck spinner.
        .catch(() => undefined);
      // A 5xx resolves undefined through the global interceptor — same thing.
      const data = response?.data;
      // /api/users/by-type/{type} returns { items: [User] }; /api/users/sales-admins [{ id, name }].
      const rows = Array.isArray(data) ? data : data?.items ?? [];
      // `type` travels with the option so the edit link opens the right Users CRM tab.
      const users = rows.map((user) => ({ id: user.id, name: userOptionName(user), type: user.type }));
      setSeen((prev) => mergeById(prev, users));
      return users.map(toUi);
    },
    [optionsUrl, toUi]
  );

  const selectedUser = known.find((o) => o.id == storedId);

  const handleChange = (_id, option) => {
    const user = option?.raw;
    if (!user) return;
    setSeen((prev) => mergeById(prev, [user]));
    if (onChange) onChange(user, user);
    else form?.setFieldValue(field?.name, user.id);
  };

  const select = (
    <Select
      id={id}
      className={["rc-select", className].filter(Boolean).join(" ")}
      fullWidth
      label={placeholder || "User"}
      placeholder={placeholder}
      options={known.map(toUi)}
      loadOptions={loadOptions}
      value={selectedUser ? selectedUser.id : null}
      onChange={handleChange}
      disabled={disabled}
    />
  );

  if (!editable || !selectedUser) return select;

  // The trigger is a <button>, so the selected user's edit icon sits beside it.
  return (
    <span className="user-select">
      {select}
      <EditUserButton user={selectedUser} className="user-select__edit" />
    </span>
  );
};

export default UserSearchSelect;
